import { includes, some, values, without } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import roles from 'common/constants/roles';

const hasAuthorizedRoles = (userRoles) => (allowedRoles) =>
  some(userRoles, (role) => includes(allowedRoles, role));

// Inventory and invoice roles are each scoped exclusively to their own module.
// Exclude them from all general permissions so module-only users cannot access
// other modules (Patients, Orders, Work Queue, etc.).
const moduleScopedRoles = [
  roles.inventoryAdmin,
  roles.inventoryManager,
  roles.inventoryViewer,
  roles.invoiceAdmin,
];

const useRoles = () => {
  const userRoles = useSelector(({ auth }) => auth.currentTenantRoles);

  const hasAccess = useCallback(
    (allowedRoles) => hasAuthorizedRoles(userRoles)(allowedRoles),
    [userRoles]
  );

  // https://smartscripts-eng.atlassian.net/wiki/spaces/POM/pages/175865897/RBAC
  /* eslint-disable max-len */
  return useMemo(
    () => ({
      hasAuthorizedRoles: hasAuthorizedRoles(userRoles),
      // Create Order
      createOrder: hasAccess(
        without(values(roles), roles.ccc, roles.shippingUser, ...moduleScopedRoles)
      ),
      // Refill Orders
      createRefillOrder: hasAccess([
        roles.admin,
        roles.pharmacist,
        roles.pharmTech,
        roles.pharmacyProdTech,
      ]),
      // Patients List
      viewPatientsList: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      createNewPatient: hasAccess([roles.admin, roles.ccc]),
      // Patient details
      viewPatientDetails: hasAccess(
        without(values(roles), roles.shippingUser, ...moduleScopedRoles)
      ),
      editPatientProfile: hasAccess(
        without(values(roles), roles.shippingUser, ...moduleScopedRoles)
      ),
      addMedication: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      updateMedication: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      deleteMedication: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      addMedicalHistory: hasAccess(
        without(values(roles), roles.shippingUser, ...moduleScopedRoles)
      ),
      updateMedicalHistory: hasAccess(
        without(values(roles), roles.shippingUser, ...moduleScopedRoles)
      ),
      deleteMedicalHistory: hasAccess(
        without(values(roles), roles.shippingUser, ...moduleScopedRoles)
      ),
      addProvider: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      updateProvider: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      deleteProvider: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      addGuarantor: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      updateGuarantor: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      deleteGuarantor: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      addInsurance: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      updateInsurance: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      deleteInsurance: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      addPharmacy: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      updatePharmacy: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      deletePharmacy: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      updateAuthorizations: hasAccess(
        without(values(roles), roles.shippingUser, ...moduleScopedRoles)
      ),
      addComments: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      deleteComments: hasAccess([roles.admin]),
      onboardPatient: hasAccess([roles.admin, roles.ccc]),
      updatePartnerIdentityValue: hasAccess(
        without(values(roles), roles.shippingUser, ...moduleScopedRoles)
      ),
      // POM-6402: narrower than editPatientProfile, matching the setPatientContactsForPartners shield
      editPartnerContact: hasAccess([roles.admin, roles.ccc, roles.pharmacist, roles.pharmTech]),
      listPatientPrescriptions: hasAccess(
        without(values(roles), roles.shippingUser, ...moduleScopedRoles)
      ),
      runSubmitTestClaims: hasAccess([roles.admin, roles.billingUser]),
      eligibilityCheck: hasAccess([roles.admin, roles.pharmTech]),
      prescriptionVerification: hasAccess([roles.admin, roles.pharmacist]),
      addPatientAddress: hasAccess(
        without(values(roles), roles.shippingUser, ...moduleScopedRoles)
      ),
      deletePatientAddress: hasAccess(
        without(values(roles), roles.shippingUser, ...moduleScopedRoles)
      ),
      updatePatientAddress: hasAccess(
        without(values(roles), roles.shippingUser, ...moduleScopedRoles)
      ),
      listPatientOrders: hasAccess(
        without(values(roles), roles.shippingUser, ...moduleScopedRoles)
      ),
      reviewLineItem: hasAccess([roles.admin, roles.pharmacist, roles.pharmTech]),
      eCommPrescriptionEligibility: hasAccess([roles.admin, roles.pharmTech]),
      addPatientNote: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      listLineItemNotes: hasAccess(
        without(values(roles), roles.shippingUser, ...moduleScopedRoles)
      ),
      noteAction: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      viewPatientHistoryTrack: hasAccess([roles.admin, roles.pharmacist]),
      manageProductFulfillmentStatus: hasAccess([
        roles.pharmacyProdTech,
        roles.admin,
        roles.pharmTech,
      ]),
      updateAddOnQuantity: hasAccess([roles.admin, roles.pharmacist, roles.pharmTech]),
      // POM-6148: Fill Later — split a line item's quantity from the patient profile
      splitLineItemQuantity: hasAccess([
        roles.admin,
        roles.pharmacist,
        roles.pharmTech,
        roles.pharmacyProdTech,
      ]),
      // POM-6180: Fill Later — move a whole order; same roles as the moveOrderToFillLater shield
      moveOrderToFillLater: hasAccess([roles.admin, roles.pharmacist, roles.pharmTech]),
      verifyOrder: hasAccess([roles.pharmacist, roles.admin]),
      cancelOrder: hasAccess([
        roles.pharmacist,
        roles.admin,
        roles.pharmTech,
        roles.pharmacyProdTech,
      ]),
      cancelBulkLineItems: hasAccess([
        roles.pharmacist,
        roles.admin,
        roles.pharmTech,
        roles.pharmacyProdTech,
      ]),
      overrideShipByDate: hasAccess([roles.admin]),
      createOrderShipment: hasAccess([
        roles.shippingUser,
        roles.admin,
        roles.billingUser,
        roles.ccc,
        roles.pharmTech,
        roles.pharmacist,
        roles.pharmacyProdTech,
      ]),
      getPatientUsers: hasAccess([roles.admin, roles.ccc]),
      triggerPasswordResetForPEUser: hasAccess([roles.admin, roles.ccc]),
      generateBulkLabels: hasAccess([
        roles.pharmacyProdTech,
        roles.admin,
        roles.pharmacist,
        roles.pharmTech,
        roles.shippingUser,
      ]),
      markLineItemsPrinted: hasAccess([
        roles.admin,
        roles.pharmTech,
        roles.pharmacist,
        roles.pharmacyProdTech,
        roles.shippingUser,
      ]),
      editPatientPrescription: hasAccess([
        roles.admin,
        roles.pharmTech,
        roles.pharmacist,
        roles.ccc,
      ]),
      editPrescriptionAutofill: hasAccess([roles.admin, roles.pharmTech, roles.ccc]),
      editFillHistory: hasAccess([roles.admin, roles.pharmacist]),
      assertQs1RxNumber: hasAccess([roles.admin, roles.pharmacist]),
      entryComplete: hasAccess([roles.admin, roles.pharmTech]),
      markOrderForExternalProcessing: hasAccess([roles.admin, roles.pharmacist, roles.pharmTech]),
      updateOrderAddress: hasAccess(without(values(roles), ...moduleScopedRoles)),
      reverifyOrder: hasAccess([roles.pharmacist, roles.admin]),
      editOrderShipment: hasAccess([
        roles.admin,
        roles.pharmTech,
        roles.pharmacist,
        roles.shippingUser,
        roles.pharmacyProdTech,
      ]),
      needsReview: hasAccess([roles.admin, roles.pharmacist, roles.pharmTech]),
      transferOrder: hasAccess([
        roles.admin,
        roles.pharmTech,
        roles.pharmacist,
        roles.pharmacyProdTech,
      ]),
      reshipOrder: hasAccess([roles.admin, roles.pharmTech, roles.pharmacist, roles.shippingUser]),
      // WorkQueue
      viewWorkQueue: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      viewPatientQualificationQueue: hasAccess([roles.admin, roles.ccc, roles.billingUser]),
      viewInClaimEligibility: hasAccess([roles.admin, roles.pharmTech, roles.pharmacist]),
      reviewInClaimEligibility: hasAccess([roles.admin, roles.pharmacist, roles.pharmTech]),
      listOrderLineItems: hasAccess([roles.admin, roles.billingUser, roles.pharmacist]),
      listBulkLabels: hasAccess([
        roles.pharmacyProdTech,
        roles.admin,
        roles.pharmacist,
        roles.ccc,
        roles.pharmTech,
      ]),
      listOrdersForProductFulfillment: hasAccess([roles.pharmacyProdTech, roles.admin]),
      listOrders: hasAccess([roles.admin, roles.pharmacist]),
      listReadyToShipOrders: hasAccess([roles.shippingUser, roles.admin]),
      reviewAdjudication: hasAccess([roles.admin, roles.billingUser]),
      profileIncomplete: hasAccess([roles.pharmTech, roles.ccc, roles.admin]),
      completeProfile: hasAccess([roles.pharmTech, roles.ccc, roles.admin]),
      prescriptionReview: hasAccess([roles.pharmacist, roles.admin, roles.pharmTech]),
      eVisitRequests: hasAccess([roles.admin, roles.ccc]),
      rxIntakeRequests: hasAccess([roles.admin, roles.pharmacist, roles.pharmTech]),
      processedExternalOrders: hasAccess([
        roles.ccc,
        roles.admin,
        roles.pharmacist,
        roles.pharmTech,
        roles.pharmacyProdTech,
      ]),
      reconcileShipment: hasAccess([roles.admin, roles.shippingUser]),
      viewCentralFillQueue: hasAccess([roles.admin, roles.pharmacist, roles.pharmTech]),
      // POM-6099: relayed orders are POM sys-admin ONLY (locked, plan R9). roles.admin IS
      // SYS_ADMIN, which is exactly what core's isPOMAdmin resolves to -- do not widen this
      // without widening the core shield rule first, or the tab appears and then 403s.
      viewRelayedOrders: hasAccess([roles.admin]),
      viewFillLaterQueue: hasAccess([roles.admin, roles.pharmacist, roles.pharmTech]),
      manageFillLaterQueue: hasAccess([roles.admin, roles.pharmacist, roles.pharmTech]),
      // POM-6223 — the Non-Routable Orders holding queue. SYS_ADMIN ONLY, deliberately narrower
      // than the queues above it: every row is a parked prescription and the two row actions
      // either create an order that will be dispensed or end the prescription permanently. The
      // backend gate is `isPOMAdmin`; this only keeps the tab honest.
      viewNonRoutableOrders: hasAccess([roles.admin]),
      viewShipments: hasAccess([roles.admin, roles.shippingUser]),
      createManualShipment: hasAccess([roles.admin, roles.shippingUser]),
      updateOrderAssociation: hasAccess([roles.pharmacist, roles.admin, roles.pharmTech]),
      ViewOrderEventHistory: hasAccess([
        roles.admin,
        roles.pharmacist,
        roles.pharmTech,
        roles.pharmacyProdTech,
        roles.shippingUser,
      ]),
      transferFacility: hasAccess([
        roles.admin,
        roles.pharmacist,
        roles.pharmacyProdTech,
        roles.pharmTech,
      ]),
      // Events
      viewEvents: hasAccess([roles.admin]),
      // Payment transactions
      viewPaymentTransactions: false,
      // Batch Shipments (pharmacist can have batch-only access, without Shipments or Reconcile tabs)
      viewBatchShipments: hasAccess([roles.admin, roles.pharmacist, roles.shippingUser]),
      // Admin Center
      // POM-5974 — the inventory and invoice roles are here as a deliberate exception
      // to the module-scoping rule above. Partner NDC preference and Invoicing live in
      // Admin Center, and without this they could not reach them at all. Every other
      // tile is gated to roles that exclude them, so what they actually see in here is
      // their own tile(s) and nothing else.
      viewAdminCenter: hasAccess([
        roles.admin,
        roles.pharmacist,
        roles.pharmTech,
        roles.pharmacyProdTech,
        roles.invoiceAdmin,
        roles.inventoryAdmin,
        roles.inventoryManager,
        roles.inventoryViewer,
      ]),
      viewDrug: hasAccess([roles.admin, roles.pharmacist]),
      viewSystem: hasAccess([
        roles.admin,
        roles.pharmacist,
        roles.pharmTech,
        roles.pharmacyProdTech,
      ]),
      viewDrugManagement: hasAccess([roles.admin, roles.pharmacist]),
      // POM-6250: master Catalog Management, admin-only per the ticket's
      // acceptance criteria (unlike Drug Management, pharmacist is not included).
      viewCatalogManagement: hasAccess([roles.admin]),
      viewRequestStatus: hasAccess([
        roles.admin,
        roles.pharmacist,
        roles.pharmTech,
        roles.pharmacyProdTech,
      ]),
      viewSigManagement: hasAccess([roles.admin, roles.pharmacist]),
      viewReferrerManagement: hasAccess([roles.admin]),
      viewPartnerManagement: hasAccess([roles.admin]),
      viewPracticeManagement: hasAccess([roles.admin]),
      viewPartnerAndPracticeManagement: hasAccess([roles.admin]),
      bulkFillHistoryUpdate: hasAccess([roles.pharmacist, roles.admin]),
      bulkUpdateOrders: hasAccess([roles.admin]),
      generateInvoices: hasAccess([roles.admin]),
      viewInvoiceGroups: hasAccess([roles.admin, roles.invoiceAdmin]),
      // Default product pricing + per-invoice-group overrides.
      viewProductPricing: hasAccess([roles.admin, roles.invoiceAdmin]),
      viewFacilityShippingDefault: hasAccess([roles.admin]),
      viewExternalTransferPharmacy: hasAccess([roles.admin, roles.pharmTech]),
      // Certificates of Analysis: every signed-in role can browse the list and open the PDFs;
      // only sys admin can upload/replace PDFs in the public COA bucket.
      viewCoa: hasAccess(values(roles)),
      uploadCoa: hasAccess([roles.admin]),
      // Orders list
      viewOrdersList: hasAccess(without(values(roles), roles.shippingUser, ...moduleScopedRoles)),
      // Inventory
      viewInventoryModule: hasAccess([
        roles.inventoryAdmin,
        roles.inventoryManager,
        roles.inventoryViewer,
        roles.admin,
      ]),
      manualInventoryAdjust: hasAccess([roles.inventoryAdmin, roles.admin]),
      performPhysicalCount: hasAccess([roles.inventoryAdmin, roles.admin]),
      createReceivingOrder: hasAccess([roles.inventoryAdmin, roles.admin]),
      viewStockManagement: hasAccess([roles.inventoryAdmin, roles.inventoryManager, roles.admin]),
      viewVendorManagement: hasAccess([roles.inventoryAdmin, roles.inventoryManager, roles.admin]),
      // POM-6405 — Manufacturer Management: every inventory role can view; add, edit
      // and remove for everyone except Inventory Viewer (read-only).
      viewManufacturerManagement: hasAccess([
        roles.inventoryAdmin,
        roles.inventoryManager,
        roles.inventoryViewer,
        roles.admin,
      ]),
      manageManufacturers: hasAccess([roles.inventoryAdmin, roles.inventoryManager, roles.admin]),
      viewReversalReview: hasAccess([roles.inventoryAdmin, roles.inventoryManager, roles.admin]),
      verifyReceivingSplit: hasAccess([roles.inventoryAdmin, roles.inventoryManager, roles.admin]),
      editSplitQty: hasAccess([roles.inventoryAdmin, roles.inventoryManager, roles.admin]),
      addVendor: hasAccess([roles.inventoryAdmin, roles.inventoryManager, roles.admin]),
      resolveAllocation: hasAccess([roles.inventoryAdmin, roles.inventoryManager, roles.admin]),
      // Narrower than resolveAllocation: a recall rewinds ORDERS as well as stock.
      recallLotAllocations: hasAccess([roles.inventoryAdmin, roles.admin]),
      // Pharmacist is here because the rule we settled on is "whoever can see a
      // drug profile can see its partner preferences", and viewDrugManagement is
      // [admin, pharmacist]. PharmTech and PharmProdTech reach Admin Center but
      // have no drug profile access, so they are deliberately left out.
      viewPartnerNdcPreference: hasAccess([
        roles.inventoryAdmin,
        roles.inventoryManager,
        roles.inventoryViewer,
        roles.pharmacist,
        roles.admin,
      ]),
      // Same rule as the view key, applied to writing: whoever can edit a drug
      // profile can edit its partner preferences. DrugManagement has no separate
      // edit gate, so viewDrugManagement [admin, pharmacist] is an edit right, and
      // pharmacist belongs here too. Only inventoryViewer is read-only.
      managePartnerNdcPreference: hasAccess([
        roles.inventoryAdmin,
        roles.inventoryManager,
        roles.pharmacist,
        roles.admin,
      ]),
      rxScanStation: hasAccess([roles.pharmacist, roles.pharmTech, roles.admin]),
    }),
    [userRoles, hasAccess]
  );
  /* eslint-enable max-len */
};

export default useRoles;
